/**
 * Water chemistry UI component.
 */

import { State } from '../state.js';
import { WATER_PROFILES } from '../core/data.js';
import {
  calculateResidualAlkalinity,
  estimateMashPH,
  chlorideSulfateBalance,
  computeRecipeMetrics,
} from '../core/calculations.js';
import { ACIDS, acidAddition, spargeAcid } from '../core/brewtools.js';

export function setupWaterTab(recalculateCallback) {
  const baseIds = [
    'w-base-ca', 'w-ca',
    'w-base-mg', 'w-mg',
    'w-base-na', 'w-na',
    'w-base-cl', 'w-cl',
    'w-base-so4', 'w-so4',
    'w-base-hco3', 'w-hco3',
  ];

  const saltIds = [
    'w-salt-gypsum', 's-gypsum', 'salt-gypsum',
    'w-salt-cacl2', 's-cacl2', 'salt-cacl2',
    'w-salt-epsom', 's-epsom', 'salt-epsom',
    'w-salt-nacl', 's-salt', 'salt-nacl',
    'w-salt-chalk', 's-chalk', 'salt-caco3',
    'w-salt-baking', 's-baking-soda', 'salt-nahco3',
  ];

  baseIds.forEach((id) => {
    document.getElementById(id)?.addEventListener('input', () => {
      syncWaterFromUI();
      recalculateCallback();
    });
  });

  saltIds.forEach((id) => {
    document.getElementById(id)?.addEventListener('input', () => {
      syncWaterFromUI();
      recalculateCallback();
    });
  });

  const volIds = ['water-total-volume', 'w-volume'];
  volIds.forEach((id) => {
    document.getElementById(id)?.addEventListener('input', () => {
      syncWaterFromUI();
      recalculateCallback();
    });
  });

  const selIds = ['water-profile-preset', 'water-profile-select', 'mobile-water-preset'];
  selIds.forEach((id) => {
    const sel = document.getElementById(id);
    sel?.addEventListener('change', (e) => {
      const profile = WATER_PROFILES.find((p) => p.name === e.target.value);
      if (!profile) return;
      State.water.base = {
        name: profile.name,
        ca: profile.ca,
        mg: profile.mg,
        na: profile.na,
        cl: profile.cl,
        so4: profile.so4,
        hco3: profile.hco3,
      };
      syncWaterToUI();
      recalculateCallback();
    });
  });

  setupAcidControls(recalculateCallback);
}

export function populateWaterProfileSelector() {
  const selIds = ['water-profile-preset', 'water-profile-select', 'mobile-water-preset'];
  selIds.forEach((id) => {
    const sel = document.getElementById(id);
    if (!sel) return;
    sel.innerHTML = '<option value="">— Anpassad profil (Svenska städer / RO) —</option>';
    WATER_PROFILES.forEach((p) => {
      const opt = document.createElement('option');
      opt.value = p.name;
      opt.textContent = p.name;
      sel.appendChild(opt);
    });
  });
}

function getNumVal(ids, fallback = 0) {
  for (const id of ids) {
    const el = document.getElementById(id);
    if (el) {
      const val = parseFloat(el.value);
      if (!isNaN(val)) return val;
    }
  }
  return fallback;
}

function syncWaterFromUI() {
  const currentName = State.water?.base?.name;

  const newCa = getNumVal(['w-base-ca', 'w-ca'], 0);
  const newMg = getNumVal(['w-base-mg', 'w-mg'], 0);
  const newNa = getNumVal(['w-base-na', 'w-na'], 0);
  const newCl = getNumVal(['w-base-cl', 'w-cl'], 0);
  const newSo4 = getNumVal(['w-base-so4', 'w-so4'], 0);
  const newHco3 = getNumVal(['w-base-hco3', 'w-hco3'], 0);

  // Om värdena har ändrats från originalprofilens värden, markera som anpassat
  const oldBase = State.water.base;
  const valuesChanged =
    oldBase.ca !== newCa || oldBase.mg !== newMg || oldBase.na !== newNa ||
    oldBase.cl !== newCl || oldBase.so4 !== newSo4 || oldBase.hco3 !== newHco3;

  State.water.base = {
    name: valuesChanged ? 'Anpassat vatten' : (currentName || 'Kranvatten'),
    ca: newCa,
    mg: newMg,
    na: newNa,
    cl: newCl,
    so4: newSo4,
    hco3: newHco3,
  };

  State.water.salts = {
    gypsum: getNumVal(['w-salt-gypsum', 's-gypsum', 'salt-gypsum'], 0),
    calciumChloride: getNumVal(['w-salt-cacl2', 's-cacl2', 'salt-cacl2'], 0),
    epsomSalt: getNumVal(['w-salt-epsom', 's-epsom', 'salt-epsom'], 0),
    tableSalt: getNumVal(['w-salt-nacl', 's-salt', 'salt-nacl'], 0),
    chalk: getNumVal(['w-salt-chalk', 's-chalk', 'salt-caco3'], 0),
    bakingSoda: getNumVal(['w-salt-baking', 's-baking-soda', 'salt-nahco3'], 0),
  };

  State.water.volume = getNumVal(['water-total-volume', 'w-volume'], 25);
}

export function syncWaterToUI() {
  const b = State.water.base || {};
  const s = State.water.salts || {};

  const setVal = (ids, val) => {
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) el.value = val ?? 0;
    });
  };

  setVal(['w-base-ca', 'w-ca'], b.ca);
  setVal(['w-base-mg', 'w-mg'], b.mg);
  setVal(['w-base-na', 'w-na'], b.na);
  setVal(['w-base-cl', 'w-cl'], b.cl);
  setVal(['w-base-so4', 'w-so4'], b.so4);
  setVal(['w-base-hco3', 'w-hco3'], b.hco3);

  setVal(['w-salt-gypsum', 's-gypsum', 'salt-gypsum'], s.gypsum);
  setVal(['w-salt-cacl2', 's-cacl2', 'salt-cacl2'], s.calciumChloride);
  setVal(['w-salt-epsom', 's-epsom', 'salt-epsom'], s.epsomSalt);
  setVal(['w-salt-nacl', 's-salt', 'salt-nacl'], s.tableSalt);
  setVal(['w-salt-chalk', 's-chalk', 'salt-caco3'], s.chalk);
  setVal(['w-salt-baking', 's-baking-soda', 'salt-nahco3'], s.bakingSoda);

  setVal(['water-total-volume', 'w-volume'], State.water.volume || 25);
  syncAcidToUI();
}

export function renderWaterPanel(waterResult) {
  const ions = ['ca', 'mg', 'na', 'cl', 'so4', 'hco3'];
  const b = State.water.base || {};

  ions.forEach((ion) => {
    const baseVal = b[ion] || 0;
    const resVal = waterResult[ion] || 0;
    const saltVal = Math.round((resVal - baseVal) * 10) / 10;
    const el = (id) => document.getElementById(id);

    const baseEl = el(`wt-base-${ion}`);
    const saltEl = el(`wt-salt-${ion}`);
    const resEl = el(`wt-res-${ion}`) || el(`w-res-${ion}`);

    if (baseEl) baseEl.textContent = baseVal.toFixed(1);
    if (saltEl) saltEl.textContent = saltVal >= 0 ? `+${saltVal.toFixed(1)}` : saltVal.toFixed(1);
    if (resEl) resEl.textContent = `${resVal.toFixed(0)} ppm`;
  });

  // RA
  const ra = calculateResidualAlkalinity(waterResult);
  const raEl = document.getElementById('w-ra-value');
  if (raEl) raEl.textContent = `${ra.toFixed(0)} ppm CaCO₃`;
  const mobileRaEl = document.getElementById('mobile-w-ra-value');
  if (mobileRaEl) mobileRaEl.textContent = `${ra.toFixed(0)} ppm`;

  let raDesc = '';
  if (ra < -150) raDesc = 'Mycket låg – passar hoppy lagers';
  else if (ra < -50) raDesc = 'Låg – passar ljust öl, IPA';
  else if (ra < 50) raDesc = 'Neutral – allround';
  else if (ra < 150) raDesc = 'Måttlig – passar amber/porter';
  else raDesc = 'Hög – passar dunkla öl';

  const raLabel = document.getElementById('w-ra-label');
  if (raLabel) raLabel.textContent = raDesc;
  const mobileRaLabel = document.getElementById('mobile-w-ra-label');
  if (mobileRaLabel) mobileRaLabel.textContent = raDesc;

  const raPct = Math.min(100, Math.max(0, ((ra + 200) / 400) * 100));
  const raFill = document.getElementById('w-ra-fill');
  const raNeedle = document.getElementById('w-ra-needle');
  if (raFill) raFill.style.width = `${raPct}%`;
  if (raNeedle) raNeedle.style.left = `${raPct}%`;

  // pH
  const mashRatio = Number(State.equipment?.mashRatio) || 3;
  const ph = estimateMashPH(waterResult, State.fermentables, mashRatio);
  const phEl = document.getElementById('w-ph-value');
  const mobilePhEl = document.getElementById('mobile-w-ph-value');
  const hasGrain = (State.fermentables || []).some((f) => f.type !== 'sugar' && Number(f.amount) > 0);
  const phText = hasGrain ? ph.toFixed(2) : '—';
  if (phEl) phEl.textContent = phText;
  if (mobilePhEl) mobilePhEl.textContent = phText;

  let phDesc = '';
  if (ph < 5.0) phDesc = '⚠️ För lågt – kan ge syrlig smak';
  else if (ph < 5.2) phDesc = 'Lågt – passar rostat öl';
  else if (ph < 5.5) phDesc = '✓ Optimalt för de flesta öl';
  else if (ph < 5.7) phDesc = 'Lite högt – försök sänka med syra';
  else phDesc = '⚠️ För högt – kan ge mjuk smak';

  const phLabel = document.getElementById('w-ph-label');
  if (phLabel) phLabel.textContent = phDesc;
  const mobilePhLabel = document.getElementById('mobile-w-ph-label');
  if (mobilePhLabel) mobilePhLabel.textContent = phDesc;

  const phPct = Math.min(100, Math.max(0, ((ph - 4.5) / 2.0) * 100));
  const phFill = document.getElementById('w-ph-fill');
  const phNeedle = document.getElementById('w-ph-needle');
  if (phFill) {
    const good = ph >= 5.2 && ph <= 5.5;
    phFill.style.background = good ? 'var(--success)' : ph < 5.2 ? 'var(--info)' : 'var(--warning)';
    phFill.style.width = `${phPct}%`;
  }
  if (phNeedle) phNeedle.style.left = `${phPct}%`;

  renderAcidResult(waterResult, mashRatio);

  // Cl:SO4 ratio
  const bal = chlorideSulfateBalance(waterResult);
  const ratioEl = document.getElementById('w-ratio-value') || document.getElementById('w-res-ratio');
  if (ratioEl) ratioEl.textContent = `${bal.ratio.toFixed(1)} (${bal.label})`;

  const ratioLabel = document.getElementById('w-ratio-label');
  if (ratioLabel) {
    ratioLabel.textContent = bal.label;
    ratioLabel.style.color = bal.color;
  }
  const ratioPct = Math.min(100, Math.max(0, (bal.ratio / 2) * 100));
  const ratioFill = document.getElementById('w-ratio-fill');
  if (ratioFill) {
    ratioFill.style.width = `${ratioPct}%`;
    ratioFill.style.background = bal.color;
  }
}

// ---------------------------------------------------------------------------
// Mäsk-pH & syra
// ---------------------------------------------------------------------------

const ACID_DEFAULTS = { enabled: false, targetPH: 5.4, type: 'lactic', strength: 80, treatSparge: true };

/**
 * Returns acid settings, filling in defaults for recipes saved before the feature existed.
 * @returns {{ enabled: boolean, targetPH: number, type: 'lactic'|'phosphoric', strength: number, treatSparge: boolean }}
 */
export function getAcidSettings() {
  if (!State.water) State.water = {};
  State.water.acid = { ...ACID_DEFAULTS, ...(State.water.acid || {}) };
  return State.water.acid;
}

function setupAcidControls(recalculateCallback) {
  const onChange = (e) => {
    const isMobile = e?.target?.id?.startsWith('mobile-');
    const prefix = isMobile ? 'mobile-' : '';
    const otherPrefix = isMobile ? '' : 'mobile-';
    const acid = getAcidSettings();
    const typeEl = document.getElementById(`${prefix}acid-type`) || document.getElementById('acid-type');
    const [type, strength] = (typeEl?.value || 'lactic:80').split(':');
    const enabledEl = document.getElementById(`${prefix}acid-enabled`) ?? document.getElementById('acid-enabled');
    acid.enabled = !!enabledEl?.checked;
    acid.targetPH = getNumVal([`${prefix}acid-target-ph`, `${otherPrefix}acid-target-ph`, 'acid-target-ph'], 5.4);
    acid.type = type;
    acid.strength = Number(strength);
    const spargeEl = document.getElementById(`${prefix}acid-treat-sparge`) ?? document.getElementById('acid-treat-sparge');
    acid.treatSparge = !!spargeEl?.checked;
    syncAcidToUI();
    recalculateCallback();
  };
  ['acid-enabled', 'acid-type', 'acid-treat-sparge', 'mobile-acid-enabled', 'mobile-acid-type', 'mobile-acid-treat-sparge'].forEach((id) =>
    document.getElementById(id)?.addEventListener('change', onChange)
  );
  ['acid-target-ph', 'mobile-acid-target-ph'].forEach((id) =>
    document.getElementById(id)?.addEventListener('input', onChange)
  );
}

function syncAcidToUI() {
  const acid = getAcidSettings();
  ['acid-enabled', 'mobile-acid-enabled'].forEach((id) => {
    const el = document.getElementById(id);
    if (el) el.checked = acid.enabled;
  });
  ['acid-target-ph', 'mobile-acid-target-ph'].forEach((id) => {
    const el = document.getElementById(id);
    if (el && document.activeElement !== el) el.value = acid.targetPH;
  });
  ['acid-type', 'mobile-acid-type'].forEach((id) => {
    const el = document.getElementById(id);
    if (el) el.value = `${acid.type}:${acid.strength}`;
  });
  ['acid-treat-sparge', 'mobile-acid-treat-sparge'].forEach((id) => {
    const el = document.getElementById(id);
    if (el) el.checked = acid.treatSparge;
  });
  document.getElementById('acid-controls')?.classList.toggle('hidden', !acid.enabled);
  document.getElementById('mobile-acid-controls')?.classList.toggle('hidden', !acid.enabled);
}

function renderAcidResult(waterResult, mashRatio) {
  const outDesktop = document.getElementById('acid-result');
  const outMobile = document.getElementById('mobile-acid-result');
  if (!outDesktop && !outMobile) return;
  const setHtml = (html) => {
    if (outDesktop) outDesktop.innerHTML = html;
    if (outMobile) outMobile.innerHTML = html;
  };
  const acid = getAcidSettings();
  if (!acid.enabled) {
    setHtml('');
    return;
  }
  const hasGrain = (State.fermentables || []).some((f) => f.type !== 'sugar' && Number(f.amount) > 0);
  if (!hasGrain) {
    setHtml('<div class="tool-hint">Lägg till malt i receptet för att beräkna syramängd.</div>');
    return;
  }

  const res = acidAddition({
    waterProfile: waterResult,
    fermentables: State.fermentables,
    mashRatio,
    targetPH: acid.targetPH,
    acid: acid.type,
    strengthPct: acid.strength,
  });
  const acidName = `${ACIDS[acid.type]?.label || 'Syra'} ${acid.strength} %`;

  let html;
  if (res.ml <= 0) {
    html = `<div>✓ Uppskattat pH (${res.currentPH.toFixed(2)}) ligger redan på eller under målet – ingen syra behövs i mäsken.</div>`;
  } else {
    html = `
      <div class="tool-result-main">${res.ml.toFixed(1)} ml ${acidName} i mäskvattnet</div>
      <div class="tool-hint">Sänker från ca ${res.currentPH.toFixed(2)} till ${Number(acid.targetPH).toFixed(2)}.
        Alternativ: ca ${res.acidulatedMaltG} g surmalt (${res.acidulatedMaltPct.toFixed(1)} % av maltgivan).</div>`;
    if (res.acidulatedMaltPct > 10) {
      html += '<div class="tool-hint" style="color:var(--warning)">⚠️ Över 10 % surmalt kan ge märkbar syrlighet – använd hellre flytande syra.</div>';
    }
  }

  if (acid.treatSparge) {
    const spargeL = computeRecipeMetrics(State).volumes?.spargeWater || 0;
    if (spargeL > 0) {
      const sp = spargeAcid({ hco3: waterResult.hco3, spargeL, acid: acid.type, strengthPct: acid.strength });
      html += sp.ml > 0
        ? `<div style="margin-top:6px">+ <strong>${sp.ml.toFixed(1)} ml</strong> i ${spargeL.toFixed(1)} L lakvatten (till pH ≈ 5,7, minskar garvämnen).</div>`
        : '<div class="tool-hint" style="margin-top:6px">Lakvattnet har ingen alkalinitet att neutralisera.</div>';
    } else {
      html += '<div class="tool-hint" style="margin-top:6px">Ingen lakning i utrustningsprofilen (t.ex. BIAB) – lakvattensyra behövs inte.</div>';
    }
  }
  setHtml(html);
}
